import { useMemo, useState } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import RatingStars from "@/components/base/RatingStars";
import { reviewCategories, reviews, type ReviewCategory } from "@/mocks/reviews";

type FilterId = (typeof reviewCategories)[number]["id"];

export default function ReviewSection() {
  const [active, setActive] = useState<FilterId>("all");

  const filtered = useMemo(() => {
    if (active === "all") return reviews;
    return reviews.filter((item) => item.category === (active as ReviewCategory));
  }, [active]);

  const [featured, ...rest] = filtered;

  return (
    <section id="reviews" className="w-full bg-background-50 py-16 md:py-20">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="测评精选"
            title="外卖、探店、开箱与复刻，一篇文档看懂一次体验"
            description="每篇测评都记录价格、用时与真实分量，并给出可对照的评分与结论。切换标签可以只看你关心的类型。"
          />
          <div className="flex flex-wrap gap-1.5 rounded-full border border-background-200 bg-background-100 p-1">
            {reviewCategories.map((cat) => {
              const isActive = active === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActive(cat.id)}
                  aria-pressed={isActive}
                  className={`whitespace-nowrap rounded-full px-3.5 py-2 text-xs font-medium transition-colors md:text-sm ${
                    isActive
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:bg-secondary-100 hover:text-foreground-950"
                  }`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="mt-12 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-12 text-center text-sm text-foreground-500">
            这个分类暂时还没有测评，试试其他标签。
          </p>
        ) : (
          <div className="mt-10 space-y-6">
            {featured ? (
              <article className="group grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-100 lg:grid-cols-2">
                <div className="relative h-56 w-full overflow-hidden lg:h-full lg:min-h-[300px]">
                  <img
                    src={featured.image}
                    alt={`${featured.title} - 美味的快递测评配图`}
                    title={`${featured.title} 测评图片`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
                    编辑主推
                  </span>
                </div>
                <div className="flex flex-col justify-center gap-4 p-6 md:p-8">
                  <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                    <RatingStars value={featured.rating} size="md" />
                    <span className="h-3 w-px bg-background-300" aria-hidden="true" />
                    <span>{featured.author}</span>
                    <span>{featured.date}</span>
                  </div>
                  <h3 className="font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                    {featured.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-foreground-600">{featured.excerpt}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {featured.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-medium text-secondary-900"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-4 border-t border-background-200 pt-4 text-xs text-foreground-600">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-price-tag-3-line text-primary-600" aria-hidden="true" />
                      {featured.price}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-timer-line text-primary-600" aria-hidden="true" />
                      {featured.readTime}
                    </span>
                    <span className="ml-auto flex items-center gap-1.5 font-medium text-accent-700">
                      <i className="ri-award-line" aria-hidden="true" />
                      {featured.highlight}
                    </span>
                  </div>
                </div>
              </article>
            ) : null}

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {rest.map((item) => (
                <article
                  key={item.id}
                  className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-primary-300"
                >
                  <div className="relative h-44 w-full overflow-hidden">
                    <img
                      src={item.image}
                      alt={`${item.title} - 美味的快递测评配图`}
                      title={`${item.title} 测评图片`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute right-3 top-3 rounded-full bg-foreground-950/75 px-2.5 py-1 text-[11px] font-medium text-background-50">
                      {item.price}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col gap-3 p-5">
                    <RatingStars value={item.rating} />
                    <h3 className="font-heading text-base font-bold leading-snug text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="flex-1 text-xs leading-relaxed text-foreground-600">{item.excerpt}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {item.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-medium text-accent-900"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between border-t border-background-200 pt-3 text-[11px] text-foreground-500">
                      <span>{item.author}</span>
                      <span>{item.date}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}